import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { sampleService, type Sample } from '../services/sample.service';
import {
  getAxiosOrErrorMessage,
  getErrorCode,
  translateAxiosErrorMessage,
  translatePrefixedErrorMessage,
} from '../utils/error.utils';

type UsageRecordInput = {
  quantity: number;
  usageDate: string;
  purpose?: string;
  notes?: string;
  action?: string;
};

type BulkUsageRecordInput = {
  sampleId: number;
  quantity: number;
  usageDate: string;
  notes?: string;
  action?: string;
};

type UseSampleManagerMutationsArgs = {
  adminViewAll: boolean;
  t: TFunction;
  setError: (message: string) => void;
  setShowCreateModal: (show: boolean) => void;
  setShowEditModal: (show: boolean) => void;
  setShowDeleteModal: (show: boolean) => void;
  setShowBulkUsageModal: (show: boolean) => void;
  setSelectedSample: (sample: Sample | null) => void;
  setSelectedSampleIds: (ids: Set<number>) => void;
  setBulkDangerKind: (kind: 'dispose' | 'delete' | null) => void;
};

/**
 * サンプル一覧ページの作成・更新・削除・使用記録ミューテーション。
 * 成功時のみ invalidate。モーダル onHide では再取得しない。
 */
export function useSampleManagerMutations({
  adminViewAll,
  t,
  setError,
  setShowCreateModal,
  setShowEditModal,
  setShowDeleteModal,
  setShowBulkUsageModal,
  setSelectedSample,
  setSelectedSampleIds,
  setBulkDangerKind,
}: UseSampleManagerMutationsArgs) {
  const queryClient = useQueryClient();
  const adminOpt = { adminViewAll: adminViewAll || undefined };

  const createMutation = useMutation({
    mutationFn: (data: Partial<Sample>) => sampleService.create(data, adminOpt),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['samples'] });
      setShowCreateModal(false);
      setError('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Sample> }) =>
      sampleService.update(id, data, adminOpt),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['samples'] });
      await queryClient.invalidateQueries({ queryKey: ['samples', 'detail', variables.id] });
      setShowEditModal(false);
      setSelectedSample(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => sampleService.delete(id, adminOpt),
    onSuccess: async (_data, deletedId) => {
      await queryClient.invalidateQueries({ queryKey: ['samples'] });
      queryClient.removeQueries({ queryKey: ['samples', 'detail', deletedId] });
      setShowDeleteModal(false);
      setSelectedSample(null);
    },
    onError: (err: unknown) => {
      setError(translateAxiosErrorMessage(err, t, t('sample.delete_failed')));
    },
  });

  const bulkDisposeMutation = useMutation({
    mutationFn: (sampleIds: number[]) => sampleService.bulkDispose(sampleIds, adminOpt),
    onSuccess: async (_data, sampleIds) => {
      await queryClient.invalidateQueries({ queryKey: ['samples'] });
      await Promise.all(
        sampleIds.map((id) =>
          queryClient.invalidateQueries({ queryKey: ['samples', 'detail', id] })
        )
      );
      setSelectedSampleIds(new Set());
      setError('');
      setBulkDangerKind(null);
    },
    onError: (err: unknown) => {
      setError(translateAxiosErrorMessage(err, t, t('sample.bulk_dispose_failed')));
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (sampleIds: number[]) => sampleService.bulkDelete(sampleIds, adminOpt),
    onSuccess: async (_data, sampleIds) => {
      await queryClient.invalidateQueries({ queryKey: ['samples'] });
      sampleIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: ['samples', 'detail', id] });
      });
      setSelectedSampleIds(new Set());
      setError('');
      setBulkDangerKind(null);
    },
    onError: (err: unknown) => {
      setError(translateAxiosErrorMessage(err, t, t('sample.bulk_delete_failed')));
    },
  });

  const addUsageMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UsageRecordInput }) =>
      sampleService.addUsageRecord(id, data, adminOpt),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['samples'] });
      await queryClient.invalidateQueries({ queryKey: ['samples', 'detail', variables.id] });
      setError('');
    },
    onError: (err: unknown) => {
      const code = getErrorCode(err);
      const flatMessage = getAxiosOrErrorMessage(err) || '';
      if (
        code === 'ECONNABORTED' ||
        flatMessage.includes('timeout') ||
        flatMessage.includes('api.error.request_timeout')
      ) {
        setError(t('api.error.request_timeout'));
        return;
      }
      setError(translatePrefixedErrorMessage(flatMessage, t, t('sample.add_usage_record_failed')));
    },
  });

  const bulkAddUsageMutation = useMutation({
    mutationFn: async (records: BulkUsageRecordInput[]) => {
      if (records.length === 0) {
        throw new Error(t('usage_record.no_available_samples'));
      }
      return sampleService.bulkAddUsage(records, adminOpt);
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['samples'] });
      const sampleIds = response?.data?.sampleIds ?? [];
      await Promise.all(
        sampleIds.map((id) =>
          queryClient.invalidateQueries({ queryKey: ['samples', 'detail', id] })
        )
      );
      setSelectedSampleIds(new Set());
      setShowBulkUsageModal(false);
      setError('');
    },
    onError: (err: unknown) => {
      setError(
        translatePrefixedErrorMessage(
          getAxiosOrErrorMessage(err) || (err instanceof Error ? err.message : undefined),
          t,
          t('sample.bulk_usage_record_failed')
        )
      );
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    bulkDisposeMutation,
    bulkDeleteMutation,
    addUsageMutation,
    bulkAddUsageMutation,
  };
}
